import { Txt, TxtProps } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { GlobalStyles, theme } from 'lib/theme';
import React from 'react';
import {
  GestureResponderEvent,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

import reactStringReplace from 'react-string-replace';
import { Button } from './Button';
import { Icon, ICONS } from './Icon';
import { TxtAsterisk } from './TxtAsterisk';

type CardLinkButton = {
  text: string;
  onPress?: ((event: GestureResponderEvent) => void) | null;
};

export type CardProps = {
  label?: string;
  labelRight?: string;
  required?: boolean;
  invalid?: boolean;
  labelIcon?: ICONS;
  labelColor?: string;
  labelSize?: TxtProps['size'];
  labelStyle?: StyleProp<ViewStyle>;
  marginHorizontal?: boolean;
  explanation?: string | React.ReactNode;
  explanationStyle?: StyleProp<ViewStyle>;
  explanationIcon?: ICONS;
  explanationLinkText?: string;
  explanationLinkAccessibilityLabel?: string;
  explanationLinkAccessibilityHint?: string;
  explanationLinkIcon?: ICONS;
  onExplanationLinkPress?: () => void;
  labelLinkText?: string;
  onLabelLinkPress?: ((event: GestureResponderEvent) => void) | null;
  labelLinkText2?: string;
  onLabelLinkPress2?: ((event: GestureResponderEvent) => void) | null;
  labelLinkButtons?: CardLinkButton[];
  separator?: React.ReactNode | null;
  style?: StyleProp<ViewStyle>;
  bodyStyle?: StyleProp<ViewStyle>;
  expanded?: boolean;
  expandedInline?: boolean;
  expandedInlineFirst?: boolean;
  expandedInlineLast?: boolean;
  children?: React.ReactNode | React.ReactNode[];
  borderRadiusTop?: boolean;
  borderRadiusBottom?: boolean;
  withShadow?: boolean;
  contentStyle?: StyleProp<ViewStyle>;
  contentHorizontal?: boolean;
};

export const Card: React.FC<CardProps> = ({
  bodyStyle,
  children,
  explanation,
  explanationStyle,
  explanationIcon,
  explanationLinkText,
  explanationLinkAccessibilityLabel,
  explanationLinkAccessibilityHint,
  explanationLinkIcon,
  onExplanationLinkPress,
  label,
  labelRight,
  required,
  labelIcon,
  labelColor,
  labelLinkText,
  labelStyle,
  onLabelLinkPress,
  labelLinkText2,
  onLabelLinkPress2,
  labelLinkButtons,
  marginHorizontal = true,
  separator,
  style,
  expanded,
  expandedInline,
  expandedInlineFirst,
  expandedInlineLast,
  labelSize = 'small',
  borderRadiusTop = true,
  borderRadiusBottom = true,
  withShadow = true,
  contentStyle,
  contentHorizontal = false,
  invalid,
}) => {
  let resolvedLabelLinks: CardLinkButton[] = [];
  if (labelLinkButtons?.length) {
    resolvedLabelLinks = labelLinkButtons.filter(
      (item): item is CardLinkButton => !!item,
    );
  } else {
    if (onLabelLinkPress !== undefined) {
      resolvedLabelLinks.push({
        text: labelLinkText || 'No Text',
        onPress: onLabelLinkPress,
      });
    }
    if (onLabelLinkPress2 !== undefined) {
      resolvedLabelLinks.push({
        text: labelLinkText2 || 'No Text',
        onPress: onLabelLinkPress2,
      });
    }
  }
  const hasLabelLinks = resolvedLabelLinks.length > 0;
  return (
    <View
      style={[
        expandedInline
          ? { marginTop: -5, marginBottom: -5, zIndex: 1 }
          : undefined,
        expandedInlineLast ? { marginBottom: 0 } : undefined,
        expandedInlineFirst ? { marginTop: 0 } : undefined,
        style,
      ]}
    >
      {(!!label || !!labelRight || hasLabelLinks) && (
        <View
          style={[
            styles.labelContainer,
            marginHorizontal
              ? { marginHorizontal: theme.padding * 2 }
              : { marginHorizontal: theme.padding },
            labelStyle,
          ]}
        >
          {!!label && (
            <>
              {labelIcon && (
                <Icon
                  source={labelIcon}
                  color={labelColor}
                  style={{ marginRight: theme.padding }}
                />
              )}
              <Txt weight="medium" size={labelSize} style={{ flex: 1 }}>
                {label}
                {required && <TxtAsterisk />}
              </Txt>
            </>
          )}
          {!label && !!labelRight && <View style={{ flex: 1 }} />}
          {!!labelRight && <Txt size={labelSize}>{labelRight}</Txt>}
          {hasLabelLinks && (
            <ViewWithSeparators
              horizontal
              style={{ marginRight: -(theme.padding / 2) }}
            >
              {resolvedLabelLinks.map((link) => (
                <Button
                  key={link.text}
                  uppercase={false}
                  text={link.text || 'No Text'}
                  compact
                  onPress={link.onPress ?? undefined}
                  textWeight="bold"
                  textSize="small"
                  style={{ marginBottom: -3, marginTop: -3 }}
                />
              ))}
            </ViewWithSeparators>
          )}
        </View>
      )}
      {!!children && (
        <View
          style={[
            { overflow: 'visible' },
            withShadow ? styles.bodyShadow : undefined,
            borderRadiusTop ? styles.bodyBorderRadiusTop : undefined,
            borderRadiusBottom ? styles.bodyBorderRadiusBottom : undefined,
            marginHorizontal ? { marginHorizontal: theme.padding } : undefined,
          ]}
        >
          <View
            style={[
              { overflow: 'hidden' },
              expanded || expandedInline
                ? {
                    borderWidth: 1,
                    borderColor: theme.colors.blue80,
                  }
                : undefined,
              borderRadiusTop ? styles.bodyBorderRadiusTop : undefined,
              borderRadiusBottom ? styles.bodyBorderRadiusBottom : undefined,
              invalid ? GlobalStyles.invalidFieldOutline : undefined,
              bodyStyle,
            ]}
          >
            {separator !== null ? (
              <ViewWithSeparators
                separator={separator}
                style={contentStyle}
                horizontal={!!contentHorizontal}
              >
                {children}
              </ViewWithSeparators>
            ) : (
              children
            )}
          </View>
        </View>
      )}
      {!!explanation && (
        <View
          style={[
            {
              paddingHorizontal: marginHorizontal
                ? theme.padding * 2
                : theme.padding,
              paddingTop: children ? 6 : 0,
              flexDirection: 'row',
            },
            explanationStyle,
          ]}
        >
          {!!explanationIcon && (
            <Icon
              source={explanationIcon}
              size={11}
              style={{ marginTop: 3, marginRight: theme.padding / 2 }}
            />
          )}
          <Txt size="xsmall" style={styles.explanationText}>
            {typeof explanation === 'string'
              ? reactStringReplace(explanation, /\*(.*?)\*/gm, (match, i) => (
                  <Txt
                    key={i}
                    size="xsmall"
                    style={styles.explanationText}
                    weight="bold"
                  >
                    {match}
                  </Txt>
                ))
              : explanation}
          </Txt>
        </View>
      )}
      {!!explanationLinkText && onExplanationLinkPress && (
        <View style={{ marginHorizontal: theme.padding }}>
          <Button
            onPress={onExplanationLinkPress}
            icon={explanationLinkIcon}
            accessibilityHint={explanationLinkAccessibilityHint}
            accessibilityLabel={explanationLinkAccessibilityLabel}
            style={{
              marginTop: -1,
              marginBottom: -7,
            }}
            textSize="xsmall"
            textWeight="regular"
            textStyle={{ textAlign: 'left', flex: 1 }}
            numberOfLines={1}
            iconSize={11}
          >
            {explanationLinkText}
          </Button>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  bodyShadow: {
    boxShadow: [
      {
        color: theme.colors.black2,
        offsetX: 0,
        offsetY: 2,
        blurRadius: 2,
      },
    ],
  },
  bodyBorderRadiusTop: {
    borderTopLeftRadius: theme.cardBorderRadius,
    borderTopRightRadius: theme.cardBorderRadius,
  },
  bodyBorderRadiusBottom: {
    borderBottomLeftRadius: theme.cardBorderRadius,
    borderBottomRightRadius: theme.cardBorderRadius,
  },
  explanationText: {
    color: theme.colors.textBlack64,
    marginVertical: 0,
    paddingBottom: 0,
  },
  labelContainer: {
    flexDirection: 'row',
    marginBottom: 6,
    alignItems: 'center',
  },
});
